import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Calendar, Phone } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { COMPANY } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`);
        setArticle(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          navigate("/blog");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1726] text-white">
        <Header />
        <div className="pt-40 text-center text-slate-400">Loading…</div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#0B1726] text-white">
        <Header />
        <div className="pt-40 pb-20 text-center px-6">
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight mb-4">
            Article not found
          </h1>
          <p className="text-slate-400 mb-6">
            That article doesn't exist or was moved. Head back to the blog to see what's live.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const canonical = `https://alldaypowerwashing.com/blog/${article.slug}`;

  return (
    <div className="min-h-screen bg-[#0B1726] text-white" data-testid="article-page">
      <Helmet>
        <title>{`${article.title} — All Day Power Washing`}</title>
        {article.meta_description && (
          <meta name="description" content={article.meta_description} />
        )}
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={article.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {article.hero_image_url && (
          <meta property="og:image" content={article.hero_image_url} />
        )}
        {article.article_json_ld && (
          <script type="application/ld+json">
            {JSON.stringify(article.article_json_ld)}
          </script>
        )}
        {article.faq_json_ld && (
          <script type="application/ld+json">
            {JSON.stringify(article.faq_json_ld)}
          </script>
        )}
      </Helmet>

      <Header />

      <article className="pt-24 sm:pt-28 pb-16">
        {/* Hero */}
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <Link
            to="/blog"
            data-testid="article-back-blog"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-heading uppercase tracking-wider mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>

          <div className="flex items-center gap-2 text-xs text-slate-500 font-heading tracking-widest uppercase mb-4">
            <Calendar className="w-3 h-3" />
            <span>{fmtDate(article.published_at)}</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight leading-[0.98]">
            {article.title}
          </h1>

          {article.meta_description && (
            <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed border-l-2 border-[#1E88E5] pl-4">
              {article.meta_description}
            </p>
          )}
        </div>

        {article.hero_image_url && (
          <div className="max-w-5xl mx-auto px-6 sm:px-8 mt-10">
            <div className="aspect-video overflow-hidden rounded-md border border-slate-800 bg-[#142238]">
              <img
                src={article.hero_image_url}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div
          data-testid="article-body"
          className="article-body max-w-3xl mx-auto px-6 sm:px-8 mt-12"
          dangerouslySetInnerHTML={{ __html: article.content_html || "" }}
        />

        {/* CTA */}
        <div className="max-w-3xl mx-auto px-6 sm:px-8 mt-16">
          <div className="bg-[#142238] border border-[#1E88E5]/40 rounded-md p-8 text-center">
            <h3 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mb-3">
              Ready to book?
            </h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm sm:text-base">
              Get a free instant quote for your property — 60 seconds, no obligation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/quote"
                className="bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-black tracking-[0.15em] uppercase text-sm px-7 py-4 rounded-sm flex items-center gap-3 transition-all"
              >
                Get My Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={COMPANY.phoneHref}
                className="bg-transparent border-2 border-white/20 hover:border-white text-white font-heading font-bold tracking-[0.15em] uppercase text-xs px-6 py-4 rounded-sm flex items-center gap-2 transition-all"
              >
                <Phone className="w-4 h-4" />
                Call or Text {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
